import { lazy, Suspense, useState, useEffect, useCallback, useRef, useReducer } from "react";
import Navbar from "@/components/Navbar";
import Ticker from "@/components/Ticker";
import LeaderboardTable from "@/components/LeaderboardTable";
import ActivityFeed from "@/components/ActivityFeed";
import RankEstimator from "@/components/RankEstimator";
import AcceptedCoins from "@/components/AcceptedCoins";
import SeoContent from "@/components/SeoContent";
import BoostConfirmation from "@/components/leaderboard/BoostConfirmation";
import {
  IDLE_BOOST, DELTA_MS, FLIP_MS, boostReducer, climbDeltas, landedRank,
  paymentAmountForQuote, reorderForClimb, targetForRank, wait,
} from "@/components/leaderboard/climbTargets";
import { useMediaQuery } from "@/components/leaderboard/useMediaQuery";
import { entityLabel, entityPath, publicAssetUrl } from "@/lib/format";
import { getLeaderboard, getActivity, getPending, getStats, invalidatePublicData } from "@/lib/api";
import { TAXONOMY_FILTERS } from "@/lib/classification";
import { useRealtime } from "@/hooks/useRealtime";
import { Plus, Flame, Users, Zap } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const ContributeModal = lazy(() => import("@/components/ContributeModal"));
const ShareModal = lazy(() => import("@/components/ShareModal"));
const CreateEntityModal = lazy(() => import("@/components/CreateEntityModal"));

const RANGES = [
  { key: "all", label: "All Time" },
  { key: "24h", label: "24H" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
];

export default function LeaderboardPage({ category }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const chainParam = (searchParams.get("chain") || "").toLowerCase();
  const chainFilter = ["robinhood", "solana", "base"].includes(chainParam)
    ? chainParam
    : "";
  const classKey = searchParams.get("class") || "";
  // TAXONOMY_FILTERS entries (and their params objects) are module-level constants,
  // so taxonomyParams is referentially stable for a given classKey.
  const taxonomy = TAXONOMY_FILTERS.find((f) => f.key === classKey) || TAXONOMY_FILTERS[0];
  const taxonomyParams = taxonomy.params;
  const [range, setRange] = useState("all");
  const [entities, setEntities] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backEntity, setBackEntity] = useState(null);
  const [backPrefill, setBackPrefill] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [boost, dispatchBoost] = useReducer(boostReducer, IDLE_BOOST);
  const [climbDeltaMap, setClimbDeltaMap] = useState({});
  const entitiesRef = useRef([]);
  const boostRef = useRef(boost);
  const boostLockRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const pendingShareRef = useRef(null);
  const successHandoffRef = useRef(false);
  const climbRun = useRef(0);
  const desktopClimb = useMediaQuery("(min-width: 640px)");
  entitiesRef.current = entities;
  boostRef.current = boost;

  const isTokens = category === "tokens";
  const climbEnabled = range === "all" || range === "24h" || range === "7d" || range === "30d";

  const loadBoard = useCallback(async () => {
    const [boardResult, pendingResult] = await Promise.allSettled([
      getLeaderboard(
        category,
        range,
        isTokens ? (chainFilter || null) : null,
        isTokens ? (taxonomyParams || {}) : {},
      ),
      getPending(category),
    ]);
    if (boardResult.status === "fulfilled") setEntities(boardResult.value.entities);
    if (pendingResult?.status === "fulfilled") setPending(pendingResult.value);
    else setPending([]);
    setLoading(false);
  }, [category, range, chainFilter, isTokens, taxonomyParams]);

  const loadActivity = useCallback(async () => {
    try { setActivity(await getActivity()); } catch (e) {}
  }, []);

  const loadStats = useCallback(async () => {
    try { setStats(await getStats()); } catch (e) {}
  }, []);

  useEffect(() => { setLoading(true); loadBoard(); loadStats(); }, [loadBoard, loadStats]);
  useEffect(() => { loadActivity(); }, [loadActivity]);

  useEffect(() => {
    climbRun.current += 1;
    boostLockRef.current = false;
    pendingRefreshRef.current = false;
    successHandoffRef.current = false;
    pendingShareRef.current = null;
    setClimbDeltaMap({});
    dispatchBoost({ type: "reset" });
  }, [category, range, chainFilter, classKey]);

  const releaseBoostLock = useCallback(() => {
    boostLockRef.current = false;
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      loadBoard();
    }
  }, [loadBoard]);

  const startSuccessSequence = useCallback(async (result) => {
    const token = climbRun.current;
    const coinId = result?.entity?.id;
    let oldRank = result?.old_rank;
    let newRank = landedRank(result);
    const amount = result?.contribution?.usd_value;
    const requestedRank = boostRef.current.requestedRank ?? boostRef.current.targetRank;
    const stillCurrent = () => token === climbRun.current;

    const finish = async () => {
      if (!stillCurrent()) return;
      setClimbDeltaMap({});
      dispatchBoost({ type: "reset" });
      successHandoffRef.current = false;
      await invalidatePublicData(["leaderboard"]);
      await loadBoard();
      pendingRefreshRef.current = false;
      boostLockRef.current = false;
      if (pendingShareRef.current) {
        setShareData(pendingShareRef.current);
        pendingShareRef.current = null;
      }
    };

    if (range !== "all" && coinId) {
      oldRank = boostRef.current.currentRank;
      try {
        await invalidatePublicData(["leaderboard"]);
        const board = await getLeaderboard(category, range, isTokens ? (chainFilter || null) : null, isTokens ? (taxonomyParams || {}) : {});
        const landed = (board.entities || []).find((row) => row.id === coinId);
        newRank = landed?.leaderboard_rank ?? null;
      } catch {
        newRank = null;
      }
    }

    if (!coinId || oldRank == null || newRank == null || !(Number(newRank) < Number(oldRank))) {
      await finish();
      return;
    }

    boostLockRef.current = true;
    dispatchBoost({
      type: "payment_success",
      coinId,
      oldRank,
      newRank,
      requestedRank,
      amount,
    });
    const reduced = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    await wait(reduced ? 0 : FLIP_MS);
    if (!stillCurrent()) return;
    setClimbDeltaMap(climbDeltas(entitiesRef.current, coinId, oldRank, newRank));
    setEntities((current) => reorderForClimb(current, coinId, newRank));
    dispatchBoost({ type: "climb" });
    await wait(reduced ? 0 : DELTA_MS);
    await finish();
  }, [loadBoard, category, range, chainFilter, isTokens, taxonomyParams]);

  useRealtime(
    useCallback((msg) => {
      if (msg.type === "activity") {
        setActivity((prev) => [msg.event, ...prev].slice(0, 40));
        const ev = msg.event;
        if (["flip", "new_number_one", "whale"].includes(ev.kind)) {
          toast(ev.text, { className: "font-mono", duration: 5000 });
        }
      }
      if (msg.type === "leaderboard_update" && msg.category === category) {
        if (boostLockRef.current) {
          pendingRefreshRef.current = true;
          return;
        }
        invalidatePublicData(["leaderboard"]);
        invalidatePublicData(["pending"]);
        invalidatePublicData(["stats"]);
        loadBoard();
        loadStats();
      }
    }, [category, loadBoard, loadStats])
  );

  const closeContribute = () => {
    setBackEntity(null);
    setBackPrefill(null);
    if (successHandoffRef.current) return;
    if (boostRef.current.status === "processing") {
      dispatchBoost({ type: "reset" });
      releaseBoostLock();
    }
  };

  const beginPayment = (amount) => {
    const current = boostRef.current;
    if (current.status !== "confirming") return;
    const entity = entitiesRef.current.find((row) => row.id === current.coinId);
    if (!entity || amount == null) {
      dispatchBoost({ type: "reset" });
      return;
    }
    boostLockRef.current = true;
    dispatchBoost({
      type: "start_payment",
      amount,
      requestedRank: current.targetRank,
    });
    setBackPrefill(amount);
    setBackEntity(entity);
  };

  const revalidateAndMaybePay = async () => {
    const current = boostRef.current;
    if (current.status !== "confirming") return;
    dispatchBoost({ type: "revalidate_start" });
    try {
      await invalidatePublicData(["leaderboard"]);
      const board = await getLeaderboard(category, range, isTokens ? (chainFilter || null) : null, isTokens ? (taxonomyParams || {}) : {});
      const latest = boostRef.current;
      if (latest.status !== "confirming" || latest.coinId !== current.coinId || latest.targetRank !== current.targetRank) {
        return;
      }
      const entity = (board.entities || []).find((row) => row.id === current.coinId);
      const fresh = targetForRank(entity, current.targetRank);
      if (!fresh) {
        dispatchBoost({ type: "revalidate_error", message: "That rank is no longer available." });
        return;
      }
      const decision = paymentAmountForQuote(current.quotedAmount, fresh.amount_usd);
      if (decision.changed) {
        dispatchBoost({ type: "quote_changed", amount: fresh.amount_usd });
        return;
      }
      dispatchBoost({ type: "quote_unchanged" });
      beginPayment(decision.amount);
    } catch {
      dispatchBoost({ type: "revalidate_error", message: "Could not refresh the rank price." });
    }
  };

  const onSuccess = (result) => {
    const climbing = boostRef.current.status === "processing";
    if (climbing) successHandoffRef.current = true;
    setBackEntity(null);
    setBackPrefill(null);
    invalidatePublicData(["leaderboard"]);
    invalidatePublicData(["pending"]);
    invalidatePublicData(["stats"]);
    invalidatePublicData(["activity"]);
    loadActivity();
    loadStats();
    result.events?.forEach((ev) => toast.success(ev.title, { duration: 6000 }));
    if (climbing) {
      pendingShareRef.current = result;
      startSuccessSequence(result);
      return;
    }
    setShareData(result);
    loadBoard();
  };

  const confirmEntity = boost.status === "confirming"
    ? entities.find((row) => row.id === boost.coinId)
    : null;
  const confirmTarget = confirmEntity ? {
    target_rank: boost.targetRank,
    amount_usd: boost.quotedAmount,
    positions_gained: boost.positionsGained,
    overtaken: boost.overtaken || [],
  } : null;
  const frozenConfirmEntity = confirmEntity
    ? { ...confirmEntity, leaderboard_rank: boost.currentRank || confirmEntity.leaderboard_rank }
    : null;
  const successEntity = entities.find((row) => row.id === boost.coinId);

  const totalBacked = entities.reduce((s, e) => {
    if (range === "all") return s + (e.total_backed_usd || 0);
    return s + (e.window_backing_usd ?? e.trend_volume ?? 0);
  }, 0);

  return (
    <div className="min-h-screen grain">
      <Navbar category={category} />
      <Ticker events={activity} />

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-10 sm:pt-14">
        <div className="animate-fade-up">
          <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl">
            {isTokens ? (<>Back your bag.<br /><span className="text-gold">Take the top.</span></>) : (<>Back the voices<br /><span className="text-gold">you trust.</span></>)}
          </h1>
          <p className="mt-4 max-w-lg text-base text-zinc-400">
            {isTokens
              ? "The crypto leaderboard powered by the people holding it."
              : "Push the KOLs you follow to #1. Backed with stablecoins, ranked in real time."}
          </p>
        </div>

        <AcceptedCoins />

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="platform-stats">
            <Link to="/stats" className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 transition-colors hover:border-gold/40">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                <Flame className="h-3 w-3" strokeWidth={2} /> Total backed
              </div>
              <div className="font-mono text-lg font-bold text-gold">
                ${stats.total_backed_usd.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </div>
            </Link>
            <Link to="/stats" className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 transition-colors hover:border-gold/40">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                <Zap className="h-3 w-3" strokeWidth={2} /> Volume 24h
              </div>
              <div className="font-mono text-lg font-bold">
                ${stats.volume_24h.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </div>
            </Link>
            <Link to="/stats" className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 transition-colors hover:border-gold/40">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                <Users className="h-3 w-3" strokeWidth={2} /> Backers
              </div>
              <div className="font-mono text-lg font-bold">{stats.total_contributors.toLocaleString("en-US")}</div>
            </Link>
            <Link to="/stats" className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 transition-colors hover:border-gold/40">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                🔥 Flips 24h
              </div>
              <div className="font-mono text-lg font-bold">{stats.flips_24h}</div>
            </Link>
          </div>
        )}

        {entities.length > 1 && (
          <RankEstimator
            entities={entities}
            onClaim={(entity, amount) => {
              setBackPrefill(amount);
              setBackEntity(entity);
            }}
          />
        )}

        <div className="mt-8 flex w-full min-w-0 max-w-full flex-wrap items-center gap-3">
          <div className="flex max-w-full min-w-0 overflow-x-auto rounded-full border border-white/10 bg-white/[0.02] p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors sm:px-4 ${
                  range === r.key ? "bg-white text-black" : "text-zinc-500 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {isTokens && (
            <div
              className="flex max-w-full min-w-0 overflow-x-auto rounded-full border border-white/10 bg-white/[0.02] p-1"
              data-testid="chain-filter"
            >
              <button
                type="button"
                data-testid="chain-filter-all"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("chain");
                  setSearchParams(next, { replace: true });
                }}
                className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  !chainFilter ? "bg-white text-black" : "text-zinc-500 hover:text-white"
                }`}
              >
                All chains
              </button>
              <button
                type="button"
                data-testid="chain-filter-robinhood"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set("chain", "robinhood");
                  setSearchParams(next, { replace: true });
                }}
                className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  chainFilter === "robinhood" ? "bg-emerald-400 text-black" : "text-zinc-500 hover:text-white"
                }`}
              >
                Robinhood
              </button>
              <button
                type="button"
                data-testid="chain-filter-solana"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set("chain", "solana");
                  setSearchParams(next, { replace: true });
                }}
                className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  chainFilter === "solana" ? "bg-cyan-400 text-black" : "text-zinc-500 hover:text-white"
                }`}
              >
                Solana
              </button>
              <button
                type="button"
                data-testid="chain-filter-base"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set("chain", "base");
                  setSearchParams(next, { replace: true });
                }}
                className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  chainFilter === "base" ? "bg-blue-400 text-black" : "text-zinc-500 hover:text-white"
                }`}
              >
                Base
              </button>
            </div>
          )}
          {isTokens && (
            <label className="flex items-center gap-2 text-xs text-zinc-500" data-testid="class-filter">
              <span className="uppercase tracking-wider">Category</span>
              <select
                value={classKey}
                onChange={(event) => {
                  const next = new URLSearchParams(searchParams);
                  const value = event.target.value;
                  if (!value) next.delete("class");
                  else next.set("class", value);
                  setSearchParams(next, { replace: true });
                }}
                className="rounded-full border border-white/10 bg-black px-3 py-2 text-xs font-bold uppercase tracking-wide text-white"
              >
                {TAXONOMY_FILTERS.map((f) => (
                  <option key={f.key || "all"} value={f.key}>{f.label}</option>
                ))}
              </select>
            </label>
          )}
          <button
            data-testid="add-entity-button"
            onClick={() => setShowCreate(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors hover:bg-white/5"
          >
            <Plus className="h-4 w-4" strokeWidth={2} /> {isTokens ? "Add token" : "Nominate KOL"}
          </button>
          <div className="ml-auto min-w-0 text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Total backed ({range})</div>
            <div className="font-mono text-lg font-bold text-gold">
              ${totalBacked.toLocaleString("en-US", { maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard + activity */}
      <section className="mx-auto mt-6 grid max-w-6xl gap-6 px-4 pb-24 lg:grid-cols-[1fr_340px]">
        <div>
          {loading ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-xl border border-white/5 bg-white/[0.02]" />
              ))}
            </div>
          ) : (
            <>
              <LeaderboardTable
                entities={entities}
                onBack={setBackEntity}
                climbEnabled={climbEnabled}
                boost={boost}
                climbDeltaMap={climbDeltaMap}
                onPreview={(coinId) => dispatchBoost({ type: "preview", coinId })}
                onClearPreview={(coinId) => dispatchBoost({ type: "clear_preview", coinId })}
                onSelectTarget={(entity, target) => dispatchBoost({
                  type: "select_target",
                  coinId: entity.id,
                  targetRank: target.target_rank,
                  amount: target.amount_usd,
                  currentRank: entity.leaderboard_rank || entity.rank,
                  positionsGained: target.positions_gained,
                  overtaken: target.overtaken,
                })}
              />
              <div className="sr-only" aria-live="polite">
                {boost.status === "success" && boost.phase === "climb" && successEntity
                  ? `${entityLabel(successEntity)} backed and is now rank ${boost.newRank}`
                  : ""}
              </div>
            </>
          )}

          {pending.length > 0 && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-[#080808] p-4" data-testid="pending-section">
              <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gold/80">
                🆕 Coming soon <span className="text-zinc-600">· awaiting first deposit</span>
              </div>
              <div className="space-y-2">
                {pending.map((e) => {
                  const label = isTokens
                    ? (e.ticker || "?")
                    : (`@${e.handle || e.slug || "?"}`);
                  const sub = isTokens
                    ? (e.name || "")
                    : (e.display_name || e.handle || "");
                  const img = publicAssetUrl(isTokens ? e.logo : e.avatar);
                  const monogram = isTokens
                    ? (e.ticker || "?").slice(0, 2)
                    : (e.display_name || e.handle || "?").slice(0, 2).toUpperCase();
                  return (
                    <div key={e.id} data-testid={`pending-row-${e.slug}`} className="flex items-center justify-between rounded-xl border border-white/5 px-3 py-2.5">
                      <Link
                        to={entityPath(e)}
                        className="flex min-w-0 flex-1 items-center gap-3 pr-3"
                      >
                        {img
                          ? <img src={img} alt="" className="h-8 w-8 rounded-full border border-white/10 object-cover" />
                          : <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-zinc-900 font-display text-[10px] font-black">{monogram}</div>}
                        <div className="min-w-0">
                          <div className="font-display text-sm font-bold">{label}</div>
                          <div className="truncate text-[11px] text-zinc-500">{sub}</div>
                        </div>
                      </Link>
                      <button data-testid={`activate-${e.slug}`} onClick={() => setBackEntity(e)}
                        className="shrink-0 rounded-full border border-gold/40 px-3 py-1.5 text-[11px] font-bold text-gold active:scale-95">
                        Activate · $1+
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <ActivityFeed events={activity} />
          </div>
        </div>
      </section>

      <SeoContent variant={isTokens ? "tokens" : "callers"} />

      {backEntity && (
        <Suspense fallback={null}>
          <ContributeModal
            entity={backEntity}
            entities={entities}
            prefillAmount={backPrefill}
            open
            onClose={closeContribute}
            onSuccess={onSuccess}
          />
        </Suspense>
      )}
      {boost.status === "confirming" && confirmEntity && confirmTarget && (
        <BoostConfirmation
          mobile={!desktopClimb}
          entity={frozenConfirmEntity}
          target={confirmTarget}
          priceChanged={boost.priceChanged}
          freshAmount={boost.freshAmount}
          revalidating={boost.revalidating}
          error={boost.error}
          onConfirm={revalidateAndMaybePay}
          onContinue={() => beginPayment(boost.freshAmount)}
          onCancel={() => dispatchBoost({ type: "cancel" })}
        />
      )}
      {shareData && (
        <Suspense fallback={null}>
          <ShareModal
            result={shareData}
            entity={shareData.entity}
            open
            onClose={() => setShareData(null)}
          />
        </Suspense>
      )}
      {showCreate && (
        <Suspense fallback={null}>
          <CreateEntityModal
            category={category}
            open
            onClose={() => setShowCreate(false)}
            onCreated={(created) => {
              if (created && created.category === "tokens") {
                toast.info(`Send at least $1 in ${created.ticker} to list it`, { duration: 6000 });
                setBackEntity(created);
              } else {
                loadBoard();
              }
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
